import type {
  VotingDetail,
  VotingQuestionWithResults,
} from '../../../../api/DigiobcanApi';
import { shortId } from '../../../../lib/ids';
import {
  adminVotingStatus,
  downloadCsv,
  votingResultsToCsv,
  votingState,
} from '../../../../lib/votings';
import { __, formatDate, formatDateTime } from '../../../../utils';
import { PRIMARY_SWITCH_SX } from '../../podnety/ReportDetailAdminPage/DecisionSwitch';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  MarkdownPreview,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Šírka ľavého pásu (railu) zápisu hlasovania.
 *
 * Je to pás na **čítanie** - názov, popis, čísla a akcie - vedľa ktorého sa
 * vpravo pracuje s obsahom a výsledkami. Ako v profile aj v builderi je to
 * pevný, flush stĺpec s deliacou linkou vpravo, nie plávajúca karta. 430 px je
 * o tretinu viac než profilových 300: popis hlasovania má aj tri odstavce
 * a dátumový rozsah so stavom sa doň zmestí bez lámania.
 */
export const SUMMARY_RAIL_WIDTH = 430;

/**
 * Čiastočná zmena viditeľnosti hlasovania - tvar tela
 * `PATCH /admin/votings/{votingId}/visibility`. Neuvedený kľúč zostáva, ako
 * bol, takže sa posiela len to, čo úradník práve prepol.
 */
export type VotingVisibilityPatch = {
  public?: boolean;
  available_results?: boolean;
  available_incomplete_results?: boolean;
};

type Props = {
  voting: VotingDetail;
  /** Otázky s výsledkami; prázdne, kým ich server nevydá. */
  results: VotingQuestionWithResults[];
  isArchived: boolean;
  /**
   * Prepne viditeľnosť. Zápis je optimistický - volajúci prekreslí detail hneď
   * a vráti ho späť, až keď server odmietne.
   */
  onVisibilityChange: (patch: VotingVisibilityPatch) => void;
  /** `rail` je flush pás na desktope, `card` je karta nad obsahom na mobile. */
  variant: 'rail' | 'card';
  /**
   * Uzavrie hlasovanie. Prítomné len tam, kde sa to dá - otvorené hlasovanie
   * bez dátumu konca; inak sa akcia nevykreslí.
   */
  onClose?: () => void;
  /** Otvorí potvrdenie archivácie; archivované hlasovanie tlačidlo nemá. */
  onArchive: () => void;
  /**
   * Otvorí dialóg termínu uzavretia. Prítomné len na bežiacom hlasovaní -
   * draft má dátumy vo formulári nastavení a na uzavretom ani archivovanom
   * hlasovaní sa už termín nemení.
   */
  onEditSchedule?: () => void;
  /** Otvorí dialóg nastavenia reportu. Bez neho sa ceruzka pri reporte nekreslí. */
  onEditReport?: () => void;
  /** Stiahnutie reportu; `format` rozhoduje, či ide PDF alebo CSV. */
  onDownloadReport?: (format: 'pdf' | 'csv') => void;
  /** „Poslať report teraz" - beží, len keď sú adresy. */
  onSendReport?: () => void;
  sendingReport?: boolean;
  downloadingReport?: boolean;
};

/**
 * Riadok súhrnu. Hodnota **nesmie** byť `flex: 'none'` - dátumový rozsah je
 * dlhší než polovica pásu, takže by pretiekol von namiesto toho, aby sa
 * zalomil. Label sa smie zmrštiť, hodnota si berie, čo potrebuje, a obe majú
 * `minWidth: 0`, inak sa flexová položka pod svoj obsah nezmrští vôbec.
 */
const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack direction='row' alignItems='baseline' gap={1.25}>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{ flex: '1 1 auto', minWidth: 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        flex: '0 1 auto',
        minWidth: 0,
        fontWeight: 500,
        textAlign: 'right',
        lineHeight: 1.4,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

/**
 * Riadok súhrnu, ktorý sa dá prepnúť. Vzhľad je ten istý ako pri `SummaryRow`
 * (menovka vľavo, hodnota vpravo), len hodnotou je prepínač - to sú tri veci
 * v páse, ktoré nie sú údaj o hlasovaní, ale rozhodnutie úradníka, a to sa
 * doteraz dalo urobiť len pred otvorením hlasovania.
 *
 * Celý riadok je `label`, takže sa prepína aj klikom na text; prepínač preto
 * vlastný `onChange` nemá - inak by sa jeden klik započítal dvakrát. Rovnaký
 * vzor ako [[ReportVisibilityToggle]], len prepínač je **primary**, nie čierny:
 * zapnuté je tu bežný, žiadaný stav (výsledky sa zverejňujú), takže nesie tú
 * istú farbu ako zvolené rozhodnutie pri náleze.
 *
 * Vysvetlenie je v tooltipe, nie pod menovkou. V 430 px páse by tri odstavce
 * popisu (aké má formulár nastavení) odtlačili akcie pod okraj obrazovky.
 */
const SummarySwitchRow: React.FC<{
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, hint, checked, disabled, onChange }) => (
  <Tooltip title={hint} placement='left'>
    <Stack
      component='label'
      direction='row'
      alignItems='center'
      gap={1.25}
      sx={{
        minHeight: 28,
        cursor: disabled ? 'default' : 'pointer',
      }}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
    >
      <Typography
        variant='body2'
        color={disabled ? 'text.disabled' : 'text.secondary'}
        sx={{ flex: '1 1 auto', minWidth: 0 }}
      >
        {label}
      </Typography>
      <Switch
        size='small'
        checked={checked}
        disabled={disabled}
        sx={PRIMARY_SWITCH_SX}
        // Klik obsluhuje celý riadok; bez tohto by sa prepnutie spustilo dvakrát.
        onClick={(event) => event.preventDefault()}
      />
    </Stack>
  </Tooltip>
);

/**
 * Hlasovanie ako celok: veľké číslo hlasov, všeobecné údaje, popis
 * a akcie (termín uzavretia, uzavretie, export, archivácia). Vpravo od neho sa
 * už len pracuje s obsahom a výsledkami - preto tu **zoznam otázok nie je**,
 * hoci tu roky stál.
 *
 * **Je to pás, nie plávajúca karta.** Na desktope (`variant='rail'`) sa kreslí
 * ako flush stĺpec s deliacou linkou vpravo - tú istú vizuálnu reč má profil
 * aj builder hlasovania - a scrolluje sa vo vlastnom vnútri, takže tu nie je
 * `position: sticky` ani meranie skrolovacieho obalu, ktoré tu kedysi bolo.
 * Na mobile (`variant='card'`) je z neho bežná karta nad obsahom; 320 px vedľa
 * neho tam nie je kde vziať.
 */
const VotingAdminSummary: React.FC<Props> = ({
  voting,
  results,
  isArchived,
  onVisibilityChange,
  variant,
  onClose,
  onArchive,
  onEditSchedule,
  onEditReport,
  onDownloadReport,
  onSendReport,
  sendingReport = false,
  downloadingReport = false,
}) => {
  // Hodiny pri každom renderi, nie raz pri mountnutí - prečo, je
  // v [[VotingDetailAdminPage]].
  const now = new Date();
  const status = adminVotingStatus(voting, now);
  const state = votingState(voting, now);
  /**
   * Hlasovanie je za nami - uzavreté alebo archivované. Rozhoduje o dvoch
   * veciach v páse:
   *
   *  * **Čas pri publiku.** Kým sa dá hlasovať, je to „Hlasovať môžu", potom
   *    „Hlasovať mohli". Jedno znenie pre oba stavy tvrdilo pri uzavretom
   *    hlasovaní, že sa doň ešte dá vstúpiť.
   *  * **Dátum pri stave.** „Uzavreté 12.9.2026" je dátum uzavretia, ale
   *    „Otvorené 12.9.2026" by bol koniec vydávaný za stav - a ten istý dátum
   *    stojí o dva riadky nižšie ako „Otvorené do".
   */
  const isOver = state === 'closed' || state === 'archived';

  const body = (
    <>
      <Stack gap={0.25}>
        <Typography variant='caption' color='text.secondary'>
          {__('Hlasov')}
        </Typography>
        <Typography
          component='b'
          sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1 }}
        >
          {voting.votes_count}
        </Typography>
      </Stack>

      <Divider />

      {/* Dátumy sú tu **bez času**: presný čas je v pruhu stavu nad obsahom
          a dva razítka s minútami sa do pásu nezmestia ani po zalomení.

          **„Od" a „Do" sú dva riadky, nie rozsah v jednom.** Rozsah
          („11.9.2026 - 12.9.2026") sa síce zmestil, ale nedal sa prečítať po
          položkách - a pri hlasovaní bez konca z neho zostalo „od 11.9.2026",
          teda riadok, ktorý o termíne uzavretia mlčal, hoci práve ten sa tu
          nastavuje. */}
      <Stack gap={1.5}>
        <SummaryRow
          label={__('Stav')}
          value={
            voting.open_to && isOver
              ? `${status.label} ${formatDate(new Date(voting.open_to))}`
              : status.label
          }
        />
        {voting.open_from && (
          <SummaryRow
            label={__('Otvorené od')}
            value={formatDate(new Date(voting.open_from))}
          />
        )}
        <SummaryRow
          label={__('Otvorené do')}
          value={
            /* Ceruzka je **v riadku termínu uzavretia**, takže je jasné, čo
               mení - vlastný riadok s akciou by v páse bol akcia bez údaja.
               Kreslí sa len na bežiacom hlasovaní; inde ju volajúci nepodá.

               `component='span'` je nutnosť, nie štýl - hodnotu riadku nesie
               `Typography`, teda `<p>`, do ktorého `<div>` nepatrí. */
            <Stack
              component='span'
              direction='row'
              alignItems='center'
              justifyContent='flex-end'
              gap={0.5}
            >
              <Box
                component='span'
                sx={{ color: voting.open_to ? undefined : 'text.secondary' }}
              >
                {voting.open_to
                  ? formatDate(new Date(voting.open_to))
                  : __('bez termínu')}
              </Box>
              {onEditSchedule && (
                <Tooltip
                  title={
                    voting.open_to
                      ? __('Zmeniť termín uzavretia')
                      : __('Doplniť termín uzavretia')
                  }
                  placement='left'
                >
                  <IconButton
                    clear
                    rounded
                    aria-label={__('Zmeniť termín uzavretia')}
                    onClick={onEditSchedule}
                    sx={{ width: 26, height: 26, mr: -0.5 }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          }
        />
        <SummaryRow
          label={isOver ? __('Hlasovať mohli') : __('Hlasovať môžu')}
          value={
            voting.allow_anonymous
              ? __('Ktokoľvek')
              : voting.verified_accounts_only
                ? __('Overené účty')
                : __('Prihlásení')
          }
        />
        {/* Zoznam otázok tu **nie je** a nemá byť: tie isté otázky stoja
            vpravo v zápise hlasovania (alebo vo výsledkoch). Zostáva z nich
            počet, ktorý je údaj o hlasovaní, nie jeho obsah. */}
        <SummaryRow
          label={__('Počet otázok')}
          value={voting.questions.length}
        />

        {/* Report je v páse ako údaj s ceruzkou, rovnako ako termín uzavretia -
            je to rozhodnutie, ktoré sa mení aj na bežiacom hlasovaní. */}
        <SummaryRow
          label={__('Report po uzavretí')}
          value={
            <Stack
              component='span'
              direction='row'
              alignItems='center'
              justifyContent='flex-end'
              gap={0.5}
            >
              <Box
                component='span'
                sx={{
                  color: voting.report_enabled ? undefined : 'text.secondary',
                }}
              >
                {voting.report_enabled
                  ? `${voting.report_emails?.length ?? 0} ${__('adries')}`
                  : __('vypnutý')}
              </Box>
              {onEditReport && (
                <Tooltip title={__('Nastaviť report')} placement='left'>
                  <IconButton
                    clear
                    rounded
                    aria-label={__('Nastaviť report')}
                    onClick={onEditReport}
                    sx={{ width: 26, height: 26, mr: -0.5 }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          }
        />

        {/* Stav odoslania je vlastný riadok, nie doplnok predošlého: úradník
            sa pýta „odišlo to?" a odpoveď má byť vidieť bez rozklikávania.
            `report_sent_at` znamená **odovzdané mailovej službe**, nie
            doručené - doručenie vie povedať až SMTP a to sa do appky
            nevracia. */}
        {voting.report_enabled && (
          <SummaryRow
            label={__('Stav reportu')}
            value={
              voting.report_sent_at
                ? formatDateTime(new Date(voting.report_sent_at))
                : isOver
                  ? __('čaká na odoslanie')
                  : __('odíde po uzavretí')
            }
          />
        )}
      </Stack>

      <Divider />

      {/* Tri rozhodnutia, nie tri údaje - preto prepínače a nie text vpravo.
          Obsah hlasovania sa po otvorení meniť nedá (`VOTING_ALREADY_ACTIVE`),
          ale **toto sa meniť musí**: či sa výsledky zverejnia, sa rozhoduje
          práve vtedy, keď hlasovanie beží alebo je uzavreté. Vlastný endpoint
          na to je `PATCH /admin/votings/{votingId}/visibility`.

          Poradie je to isté ako v nastaveniach hlasovania - „či" nad „kedy" -
          a priebežné výsledky sú vetva prístupu: pri vypnutom prístupe riadok
          zostáva (aby sa pás pri prepínaní nepreskladal), ale je neaktívny
          a zhasnutý. Kombináciu „nezverejňovať, ale priebežne áno" zakazuje
          CHECK v databáze aj API. */}
      <Stack gap={1.5}>
        <SummarySwitchRow
          label={__('Detail vidia aj neprihlásení')}
          hint={
            voting.allow_anonymous
              ? __(
                  'Pri hlasovaní pre kohokoľvek je verejná viditeľnosť vynútená - inak by sa k nemu neprihlásený nedostal.',
                )
              : __('Hlasovanie sa dá otvoriť z odkazu bez prihlásenia.')
          }
          // Pri publiku „ktokoľvek" je zapnuté natvrdo, rovnako ako vo
          // formulári nastavení; vypnuté by hlasovanie skrylo pred publikom,
          // pre ktoré bolo zvolené.
          checked={voting.allow_anonymous ? true : !!voting.public}
          disabled={isArchived || voting.allow_anonymous}
          onChange={(checked) => onVisibilityChange({ public: checked })}
        />
        <SummarySwitchRow
          label={__('Výsledky pre občana')}
          hint={
            voting.available_results
              ? __('Zapnuté: výsledky sa zverejnia po uzavretí hlasovania.')
              : __(
                  'Vypnuté: občan výsledky neuvidí ani po uzavretí, zostane mu len počet hlasov. Vy ich v správe hlasovaní vidíte ďalej.',
                )
          }
          checked={!!voting.available_results}
          disabled={isArchived}
          onChange={(checked) =>
            onVisibilityChange(
              // Vypnutie strhne aj priebežné výsledky. Posiela sa to výslovne,
              // nie sa to necháva na server: v jednej odpovedi sa tak vrátia
              // obe hodnoty a optimistický zápis nemá čo dobiehať.
              checked
                ? { available_results: true }
                : {
                    available_results: false,
                    available_incomplete_results: false,
                  },
            )
          }
        />
        <SummarySwitchRow
          label={__('Priebežné výsledky')}
          hint={
            !voting.available_results
              ? __('Bez prístupu k výsledkom nie je čo zverejňovať priebežne.')
              : voting.available_incomplete_results
                ? __(
                    'Zapnuté: podrobné výsledky sú viditeľné počas celého hlasovania.',
                  )
                : __(
                    'Vypnuté: počas hlasovania je viditeľný len počet hlasov. Podrobnosti sa zverejnia po uzavretí.',
                  )
          }
          checked={
            voting.available_results
              ? !!voting.available_incomplete_results
              : false
          }
          disabled={isArchived || !voting.available_results}
          onChange={(checked) =>
            onVisibilityChange({ available_incomplete_results: checked })
          }
        />
      </Stack>

      {/* Popis stojí **pod** číslami a údajmi, nie nad nimi: pás sa číta
          zhora ako „koľko hlasov a za akých pravidiel", a až potom prichádza
          text, ktorý má aj tri odstavce. Názov je v hlavičke stránky, nie tu,
          takže popis má vlastný label. */}
      {voting.description && (
        <>
          <Divider />
          <Stack gap={0.5}>
            <Typography variant='caption' color='text.secondary'>
              {__('Popis')}
            </Typography>
            {/* **Popis je markdown**, ten istý, aký občan vidí na detaile
                hlasovania - takže sa aj tu kreslí `MarkdownPreview`-om, nie ako
                obyčajný text. Kým to bol text, svietila v páse samotná syntax
                (`**dôležité**`, `##`, odkazy v hranatých zátvorkách) a odkaz na
                presov.sk sa nedal klikať.

                Margin prvého a posledného bloku sa nuluje: odstupy v páse drží
                `gap` stĺpca a markdown by k nim pridal ešte svoj vlastný. */}
            <Box
              sx={{
                '& .wmde-markdown': {
                  background: 'transparent',
                  fontSize: 14,
                },
                '& .wmde-markdown > :first-of-type': { marginTop: 0 },
                '& .wmde-markdown > :last-child': { marginBottom: 0 },
              }}
            >
              <MarkdownPreview
                value={voting.description}
                style={{ background: 'transparent', fontSize: 14 }}
              />
            </Box>
          </Stack>
        </>
      )}

      {/* Uzavretie ako výrazné (tmavé) tlačidlo pod popisom - kreslí sa len
          keď ho volajúci podá (na mobile v karte; na desktope ho namiesto pásu
          nesie pripnutá pätička pravej plochy). */}
      {onClose && (
        <>
          <Divider />
          <Button variant='dark' fullWidth onClick={onClose}>
            {__('Uzavrieť hlasovanie')}
          </Button>
        </>
      )}

      {/* Export má zmysel len tam, kde sú výsledky - kým hlasovanie beží (alebo
          po archivácii) by CSV bolo prázdne, tak sa trojica ani nekreslí.

          **CSV sa naďalej skladá v prehliadači**, hoci ho vie vydať aj server:
          dáta na stránke už sú, takže stiahnutie je okamžité a nepotrebuje
          ďalšie kolo na server. PDF naopak z prehliadača vyrobiť nejde - sadzí
          ho `@react-pdf/renderer` na serveri, takže je to stiahnutie
          z `GET /admin/votings/{id}/report`. Nahradilo tlačidlo „Tlač"
          (`window.print()`), z ktorého vychádzala stránka appky, nie
          dokument. */}
      {!isArchived && results.length > 0 && (
        <>
          <Divider />
          <Stack gap={1}>
            <Button
              variant='outline'
              startIcon={<DownloadIcon fontSize='small' />}
              onClick={() =>
                downloadCsv(
                  `hlasovanie-${shortId(voting.voting_id)}.csv`,
                  votingResultsToCsv(voting, results),
                )
              }
            >
              {__('Export CSV')}
            </Button>
            {onDownloadReport && (
              <Button
                variant='outline'
                startIcon={<PictureAsPdfIcon fontSize='small' />}
                loading={downloadingReport}
                onClick={() => onDownloadReport('pdf')}
              >
                {__('Stiahnuť PDF')}
              </Button>
            )}
            {/* „Poslať teraz" má zmysel len keď je komu. Nevyžaduje zapnutý
                prepínač - report sa má dať vyskúšať ešte pred jeho zapnutím -
                ale bez adries by server vrátil `VOTING_REPORT_NO_RECIPIENTS`. */}
            {onSendReport && (voting.report_emails?.length ?? 0) > 0 && (
              <Button
                variant='outline'
                startIcon={<MailOutlineIcon fontSize='small' />}
                loading={sendingReport}
                onClick={onSendReport}
              >
                {__('Poslať report teraz')}
              </Button>
            )}
          </Stack>
        </>
      )}

      {/* Archivácia je na samom spodku a za linkou - je nevratná a nie je to
          bežný krok životného cyklu. Varovanie je krátke, plné znenie nesie
          potvrdenie, ktoré tlačidlo otvorí. */}
      {!isArchived && (
        <>
          <Divider />
          <Stack gap={0.75} alignItems='flex-start'>
            <Typography
              component='b'
              sx={{ fontSize: 13, fontWeight: 700, color: 'primary.extraDark' }}
            >
              {__('Archivovať hlasovanie')}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {__(
                'Zmizne občanom, zmažú sa voľné odpovede aj záznamy o účasti a podrobné výsledky sa prestanú zobrazovať aj v administrácii. Zostane počet hlasov. Vrátiť sa to nedá.',
              )}
            </Typography>
            <Button variant='danger' size='small' onClick={onArchive}>
              {__('Archivovať')}
            </Button>
          </Stack>
        </>
      )}
    </>
  );

  // Rail nekreslí vlastný rám ani podklad - tie nesie obal v `VotingLockedView`
  // (flush pás s deliacou linkou vpravo). Na mobile je to bežná karta.
  return variant === 'rail' ? (
    <Stack gap={2} sx={{ p: 2.5 }}>
      {body}
    </Stack>
  ) : (
    <Card sx={{ p: 2.5, gap: 2 }}>{body}</Card>
  );
};

export default VotingAdminSummary;
